import { styleConstants } from '@paywithextend/components'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMerchantCategoryRestrictionsState } from 'src/app/AccountSettings/AccountSettings.mcc'
import {
  selectCreditCardIdFromRoute,
  selectPatchingCreditCardFromState,
  selectPutCreditCardAccountCausedErrorFromState,
  selectPutCreditCardBillingCausedErrorFromState,
  selectPutCreditCardErrorDetailMapFromState,
  selectPutCreditCardTopLevelErrorFromState,
  selectPuttingCreditCardAccountFromState,
  selectPuttingCreditCardBillingFromState,
  selectPuttingCreditCardFromState,
  selectReplaceCreditCardErrorDetailMapFromState,
  selectReplaceCreditCardTopLevelErrorFromState,
} from 'src/app/AccountSettings/redux/accountSettings.selectors'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import AccountBillingInformationBox, {
  AccountBillingInformationCountryFields,
  AccountBillingInformationFlatFields,
} from 'src/components/AccountBillingInformationBox/AccountBillingInformationBox'
import AccountInformationBox, {
  AccountInformationFlatFields,
} from 'src/components/AccountInformationBox/AccountInformationBox'
import AccountVerificationBlockedBox from 'src/components/AccountVerificationBlockedBox/AccountVerificationBlockedBox'
import AccountVerificationRequiredBox from 'src/components/AccountVerificationRequiredBox/AccountVerificationRequiredBox'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import CardInformationBox, {
  CardInformationBoxFlatFields,
} from 'src/components/CardInformationBox/CardInformationBox'
import HorizontalContentCollection from 'src/components/HorizontalContentCollection/HorizontalContentCollection'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import TestId from 'src/components/TestId/TestId'
import VerticalContentCollection from 'src/components/VerticalContentCollection/VerticalContentCollection'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { SupportedBrands } from 'src/enums'
import type { PhysicalCardLifecycleControls } from 'src/feature/PhysicalCardLifecycle/physicalCardLifecycle.types'
import { unavailablePhysicalCardLifecycleAvailability } from 'src/feature/PhysicalCardLifecycle/physicalCardLifecycleAvailability'
import PhysicalCardTransactionsPanel from 'src/feature/PhysicalCardTransactionsPanel/PhysicalCardTransactionsPanel'
import UpdatedPhysicalCardSettings, {
  PaymentChannelId,
  UpdatedBillingContact,
} from 'src/feature/UpdatedPhysicalCardSettings/UpdatedPhysicalCardSettings'
import VirtualCardCapabilitiesPanel from 'src/feature/VirtualCardCapabilitiesPanel/VirtualCardCapabilitiesPanel'
import { useBackgroundDispatch, useDebounceValue, usePrevious } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { PhysicalCardStatus } from 'src/models/creditCard.models'
import { UserFeature } from 'src/models/user.models'
import { isUnprovisioned } from 'src/page-components/Accounts/cardManagementSource'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import {
  countriesQueries,
  toCountryOptions,
} from 'src/queries/countries/countries.queries'
import { shouldShowCvsVerificationFailedTakeover } from 'src/queries/creditCard.derivations'
import {
  useQueryCreditCard,
  useQueryCreditCardPermissions,
} from 'src/queries/creditCard.queries'
import {
  useStartCreditCardVerification,
  useUpdateCreditCard,
  useVerifyCreditCard,
} from 'src/queries/creditCards/creditCardWrites.queries'
import type { QueryError } from 'src/queries/network.utils'
import type { PhysicalCardManagementStatus } from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import {
  cardManagementActionReasonCode,
  isCardManagementActionAvailable,
} from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import {
  physicalCardManagementQueries,
  useChangeGeofenceCountry,
  useCreatePermanentLimitChange,
  useReplaceBillingContact,
  useSetPaymentChannel,
} from 'src/queries/physicalCardManagement/physicalCardManagement.queries'
import {
  CARD_MANAGEMENT_ERROR_CODES,
  cardManagementErrorCode,
  createCardManagementIdempotencyKey,
} from 'src/queries/physicalCardManagement/physicalCardManagement.utils'
import { creditCardsQueryKeys } from 'src/queries/queries.constants'
import {
  PUT_CREDIT_CARD_ACCOUNT_INIT,
  PUT_CREDIT_CARD_BILLING_INIT,
  putCreditCard,
  replaceCreditCard,
} from 'src/redux/actions/data/creditCards.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  isCardDetailsConfirmationEnabled,
  isCardDetailsConfirmationRetryEnabled,
  isUpdatedPhysicalCardSettingsEnabled,
} from 'src/utils/featureFlags'
import { useWhitelabel } from 'src/utils/whitelabel.utils'
import { css } from 'styled-components'

type AccountSettingsProps = {
  cardManagementStatus?: PhysicalCardManagementStatus
  lifecycleControls?: PhysicalCardLifecycleControls
}

/** One request per typing pause, not one per keystroke. */
const GEOFENCE_SEARCH_DEBOUNCE_MS = 300

const unavailableLifecycleControls: PhysicalCardLifecycleControls = {
  availability: unavailablePhysicalCardLifecycleAvailability,
  openAction: () => undefined,
  isActionInProgress: () => false,
  canToggleFreeze: false,
  freezeAction: undefined,
  toggleFreeze: () => undefined,
  isFreezePending: false,
}

const AccountSettings: React.FC<AccountSettingsProps> = ({
  cardManagementStatus,
  lifecycleControls = unavailableLifecycleControls,
}) => {
  const translate = useTranslate()

  const creditCardId = useSelector(selectCreditCardIdFromRoute)
  const { data: creditCard, refetch: refetchCreditCard } = useQueryCreditCard({
    creditCardId: creditCardId,
    enabled: !!creditCardId,
  })
  const { data: creditCardPermissions } = useQueryCreditCardPermissions({
    creditCardId: creditCard?.id,
    enabled: !!creditCard?.id,
    retry: false,
  })

  const { data: userFeatures } = useQueryUserFeatures([
    UserFeature.PHYSICAL_CARD_TRANSACTIONS_FEED,
  ])
  const physicalCardTransactionsFeedEnabled =
    userFeatures?.[UserFeature.PHYSICAL_CARD_TRANSACTIONS_FEED] === true
  const physicalCardStatus = creditCard?.physicalCard?.status
  const canConnect = physicalCardTransactionsFeedEnabled
  const remoteConfig = useSelector(selectFeatureFlaggerFromState)
  const confirmCardDetailsEnabled =
    isCardDetailsConfirmationEnabled(remoteConfig)
  // EX-40694 fast-follow kill switch, AND-ed with the parent flag so the retry
  // surfaces can never outlive the confirm flow they route into.
  const confirmCardDetailsRetryEnabled =
    confirmCardDetailsEnabled &&
    isCardDetailsConfirmationRetryEnabled(remoteConfig)
  // Flag off = treat the card as having nothing confirmable, which collapses
  // every carve-out below (and the prop-threaded ones in the panel/tile) back
  // to the pre-EX-40639 behavior for the missing-details population.
  const missingCardDetails = confirmCardDetailsEnabled
    ? (creditCard?.physicalCard?.missingCardDetails ?? [])
    : []
  // A null status normally means "not eligible right now," but the API
  // deliberately returns status=null (rather than ENROLLMENT_AVAILABLE) for a
  // card that's confirmable-but-missing-details (see
  // PhysicalCardEnrollmentEligibilityService.isEnrollable vs.
  // missingEnrollmentDetails) — without this carve-out this legacy branch
  // hides the panel entirely for the exact population EX-40639 exists to
  // serve. Mirrors the identical fix in PhysicalCardTransactionsPanel.tsx.
  const isConfirmableMissingDetails =
    missingCardDetails.length > 0 && canConnect
  const hasPhysicalCard = !!physicalCardStatus || isConfirmableMissingDetails
  const isPhysicalCardActive = physicalCardStatus === PhysicalCardStatus.Active

  // Gate the panel on the user feature, with an escape hatch (Active card →
  // still show, but only to allow disconnect).
  const showPhysicalCardTransactionsPanel =
    hasPhysicalCard &&
    (physicalCardTransactionsFeedEnabled || isPhysicalCardActive)

  const verifyMutation = useVerifyCreditCard()
  const startVerificationMutation = useStartCreditCardVerification()
  const updateCreditCardMutation = useUpdateCreditCard()

  const { whiteLabelIncludes } = useWhitelabel()

  const isExtendBrand = whiteLabelIncludes(SupportedBrands.Extend)

  const currentUser = useCurrentUser()

  // Capabilities gate limits and freeze — unlike billing contact, these DO have
  // contract actions. Read once and keyed per action: enrollment, currency
  // support and update-access are evaluated separately per action on the
  // server, so one verdict is never a proxy for another.
  //
  // EX-40599: hoisted above the surface gate, and no longer gated BY it. The
  // gate now ORs in this response's demo-enrollment verdict, so leaving
  // `showUpdatedPhysicalCardSettings` in `enabled` would be circular — the query
  // would wait for a gate that is waiting for the query, and an enrolled org
  // with the flag off would never leave the legacy boxes.
  //
  // Dropping the gate from `enabled` costs no extra request: `AccountEntityPage`
  // already fetches this exact key unconditionally for its own hero and tab
  // gates, so this resolves against the same cache entry.
  const capabilitiesQuery = useQuery({
    ...physicalCardManagementQueries.capabilities(creditCard?.id),
    enabled: Boolean(creditCard?.id),
  })
  const capabilities = capabilitiesQuery.data?.capabilities

  const showUpdatedPhysicalCardSettings = isUpdatedPhysicalCardSettingsEnabled(
    remoteConfig,
    capabilities,
  )

  // The redesigned tiles are props-only (src/AGENTS.md layering), so the page
  // resolves the reference data, gates and side effects they need.
  const locale = useSelector(selectNullSafeLocaleFromState)
  const { data: countryOptions = [] } = useQuery({
    ...countriesQueries.list(locale),
    select: toCountryOptions,
    enabled: showUpdatedPhysicalCardSettings && Boolean(locale),
  })
  const merchantCategoryRestrictions = useMerchantCategoryRestrictionsState({
    enabled: showUpdatedPhysicalCardSettings,
    creditCardId: creditCard?.id,
  })

  const canReadLimits = isCardManagementActionAvailable(
    capabilities,
    'READ_PHYSICAL_CARD_LIMIT',
  )
  const canApplyLimitChange = isCardManagementActionAvailable(
    capabilities,
    'APPLY_PHYSICAL_CARD_LIMIT_CHANGE',
  )
  // Billing & contact. Read and write gate independently — a Card Manager holds
  // READ_PHYSICAL_CARD_BILLING while MANAGE_PHYSICAL_CARD_BILLING comes back
  // ROLE_NOT_ALLOWED, which is exactly the tile's read-only render.
  // `isCardManagementActionAvailable` fails closed, so a PENDING capabilities
  // read is indistinguishable from a denial. Without this, the billing form
  // would render its degraded card-backed source (and an operable Save routing
  // to PUT /creditcards/:id) during the load window, then swap underneath the
  // user once capabilities resolved to the Card Management path.
  const capabilitiesResolved =
    capabilities !== undefined || capabilitiesQuery.isError

  const canReadBillingContact = isCardManagementActionAvailable(
    capabilities,
    'READ_PHYSICAL_CARD_BILLING',
  )
  const canManageBillingContact = isCardManagementActionAvailable(
    capabilities,
    'MANAGE_PHYSICAL_CARD_BILLING',
  )
  // Distinguishes "your role cannot" from "not available on this card" in the
  // read-only copy — the capability check alone collapses every reason to false.
  const billingEditDeniedReason = cardManagementActionReasonCode(
    capabilities,
    'MANAGE_PHYSICAL_CARD_BILLING',
  )

  // EX-40631 — the API-backed billing & contact editor.
  //
  // No separate client flag: the whole redesigned surface is already behind
  // `show_updated_physical_card_settings`, and the server's own capability
  // verdict is the real gate. `isCardManagementActionAvailable` fails closed, so
  // a card whose backend has not activated this family simply keeps the legacy
  // billing form.
  const showBillingContactApi =
    showUpdatedPhysicalCardSettings && canReadBillingContact

  // Payment channels. Read and manage gate independently, and the read itself
  // also carries a PER-CHANNEL availability the tile applies on top.
  const canReadPaymentChannels = isCardManagementActionAvailable(
    capabilities,
    'READ_PHYSICAL_CARD_PAYMENT_CHANNELS',
  )
  // Gated independently of the read, like billing. The per-channel
  // `availability` already carries the server's finer verdict, but this is the
  // same capability the PUT authorizes against, so a denied MANAGE must not
  // leave switches operable if the collection still reports AVAILABLE.
  const canManagePaymentChannels = isCardManagementActionAvailable(
    capabilities,
    'MANAGE_PHYSICAL_CARD_PAYMENT_CHANNELS',
  )

  const paymentChannelsQuery = useQuery({
    ...physicalCardManagementQueries.paymentChannels(creditCard?.id),
    enabled:
      showUpdatedPhysicalCardSettings &&
      Boolean(creditCard?.id) &&
      canReadPaymentChannels,
  })

  const setPaymentChannel = useSetPaymentChannel()

  const paymentChannelsState: 'loading' | 'ready' | 'unavailable' | 'error' =
    ((): 'loading' | 'ready' | 'unavailable' | 'error' => {
      if (capabilitiesQuery.isError) return 'error'
      if (capabilities === undefined) return 'loading'
      if (!canReadPaymentChannels) return 'unavailable'
      if (paymentChannelsQuery.data !== undefined) return 'ready'
      return paymentChannelsQuery.isError ? 'error' : 'loading'
    })()

  const onSetPaymentChannel = useCallback(
    (
      channel: PaymentChannelId,
      enabled: boolean,
      expectedVersion: number,
    ): void => {
      const id = creditCard?.id
      if (id === undefined) return
      // Idempotent SET, not a toggle: the desired value is sent outright, so a
      // replayed key cannot invert the channel.
      setPaymentChannel.mutate({
        creditCardId: id,
        channel,
        request: { enabled, expectedVersion },
        idempotencyKey: createCardManagementIdempotencyKey(),
      })
    },
    [creditCard?.id, setPaymentChannel],
  )

  // Geofence (EX-40629). Read and manage gate INDEPENDENTLY, exactly like
  // payment channels: the `roleRestricted` contract fixture returns READ
  // AVAILABLE with MANAGE UNAVAILABLE/ROLE_NOT_ALLOWED, and the geofence GET
  // still hands that actor an AVAILABLE snapshot — so trusting the snapshot
  // alone would render an editable drawer whose every POST 403s.
  const canReadGeofence = isCardManagementActionAvailable(
    capabilities,
    'READ_PHYSICAL_CARD_GEOFENCE',
  )
  // Same capability the POST authorizes against. The snapshot's own
  // `availability` carries the server's finer verdict and is still applied by
  // the tile, but this is what decides whether a write is offered at all.
  const canManageGeofence = isCardManagementActionAvailable(
    capabilities,
    'MANAGE_PHYSICAL_CARD_GEOFENCE',
  )

  const geofenceQuery = useQuery({
    ...physicalCardManagementQueries.geofence(creditCard?.id),
    // Declarative `enabled`: a denied capability must never fire a request the
    // backend will reject. The spread replaces the factory's arg-presence
    // guard, so it is re-stated.
    enabled:
      showUpdatedPhysicalCardSettings &&
      Boolean(creditCard?.id) &&
      canReadGeofence,
  })

  const [geofenceCountryQuery, setGeofenceCountryQuery] = useState('')
  // Debounced so a typed query is one request per pause, not one per keystroke.
  // The debounced value is what reaches the query KEY, so intermediate strings
  // never become cache entries either.
  const debouncedGeofenceCountryQuery = useDebounceValue(
    geofenceCountryQuery,
    GEOFENCE_SEARCH_DEBOUNCE_MS,
  )

  // The country catalogue is CARD-SCOPED and server-authored — not the app's
  // own /countries reference list. Only the server knows which countries are a
  // valid selection for this card, and it authors the display names; sourcing
  // the picker locally would offer codes the change then rejects with
  // CARD-MGMT.23. The stage fixture is currently small (US/CA/GB), which is a
  // backend fixture limitation, not a reason to change the source.
  const geofenceCountriesQuery = useQuery({
    ...physicalCardManagementQueries.geofenceCountries(
      creditCard?.id,
      debouncedGeofenceCountryQuery,
    ),
    // Search-driven, not browse-on-open: the drawer only lists results once the
    // user has typed, so firing this on open would spend a request on a list
    // that is never rendered. The contract does allow an empty query (no
    // filter) — that is simply not the interaction the design asks for.
    enabled:
      showUpdatedPhysicalCardSettings &&
      Boolean(creditCard?.id) &&
      canManageGeofence &&
      geofenceQuery.data?.availability === 'AVAILABLE' &&
      debouncedGeofenceCountryQuery.trim() !== '',
  })

  const changeGeofenceCountry = useChangeGeofenceCountry()
  // `.mutate` is stable across renders; the mutation RESULT object is not, so
  // depending on the latter would re-mint the callback every render.
  const { mutate: changeGeofenceCountryMutate } = changeGeofenceCountry

  const geofenceState: 'loading' | 'ready' | 'unavailable' | 'error' = (():
    | 'loading'
    | 'ready'
    | 'unavailable'
    | 'error' => {
    if (capabilitiesQuery.isError) return 'error'
    if (capabilities === undefined) return 'loading'
    // A denied capability is a real answer about this card, not a transient
    // failure the user should be invited to retry.
    if (!canReadGeofence) return 'unavailable'
    if (geofenceQuery.data !== undefined) return 'ready'
    if (!geofenceQuery.isError) return 'loading'
    // 403/404 is the server saying this card is not enrolled, or the action is
    // not available to this actor — a durable answer, not something a refresh
    // fixes. Geofence has no capability entry to gate the read on yet (the
    // contract withholds those until all three routes are executable), so this
    // is what keeps a non-enrolled card from showing a permanent "Refresh to
    // try again" banner. Shares the Activity/Statements predicate so the tabs
    // on this page cannot drift on that judgement.
    return isUnprovisioned(geofenceQuery.error) ? 'unavailable' : 'error'
  })()

  const onChangeGeofenceCountry = useCallback(
    (
      change: 'ADD' | 'REMOVE',
      countryCode: string,
      expectedVersion: number,
    ): void => {
      const id = creditCard?.id
      if (id === undefined) return
      changeGeofenceCountryMutate({
        creditCardId: id,
        request: { change, countryCode, expectedVersion },
        // One key per attempt. The fingerprint covers change + code + version,
        // so a genuine retry of the same click replays rather than re-applies.
        idempotencyKey: createCardManagementIdempotencyKey(),
      })
    },
    [creditCard?.id, changeGeofenceCountryMutate],
  )

  const canReadCompanyLine = isCardManagementActionAvailable(
    capabilities,
    'READ_PHYSICAL_CARD_COMPANY_LINE',
  )

  // Composed alongside limits, never merged with it: the contract is explicit
  // that this is not an Overview aggregate, so a company-line failure must not
  // take the card-level limit down with it (and vice versa).
  const companyLineQuery = useQuery({
    ...physicalCardManagementQueries.companyLine(creditCard?.id),
    enabled:
      showUpdatedPhysicalCardSettings &&
      Boolean(creditCard?.id) &&
      canReadCompanyLine,
  })

  const limitsQuery = useQuery({
    ...physicalCardManagementQueries.limits(creditCard?.id),
    // Declarative `enabled`, not an effect: a denied capability must never fire
    // a request the backend will reject. The spread replaces the factory's
    // arg-presence guard, so it is re-stated.
    enabled:
      showUpdatedPhysicalCardSettings &&
      Boolean(creditCard?.id) &&
      canReadLimits,
  })

  const createLimitChange = useCreatePermanentLimitChange()

  const limitsState: 'loading' | 'ready' | 'unavailable' | 'error' = (():
    | 'loading'
    | 'ready'
    | 'unavailable'
    | 'error' => {
    if (capabilitiesQuery.isError) return 'error'
    if (capabilities === undefined) return 'loading'
    // A denied capability is a real answer about this card, not a transient
    // failure the user should be invited to retry.
    if (!canReadLimits) return 'unavailable'
    if (limitsQuery.data !== undefined) return 'ready'
    return limitsQuery.isError ? 'error' : 'loading'
  })()

  const onApplyPermanentLimit = useCallback(
    (permanentLimitMinorUnits: number): void => {
      const id = creditCard?.id
      const snapshot = limitsQuery.data
      // No snapshot means no version fence and no authoritative currency.
      // Guessing either is how a concurrent change gets silently overwritten.
      if (id === undefined || snapshot === undefined) return

      createLimitChange.mutate({
        creditCardId: id,
        request: {
          kind: 'PERMANENT',
          permanentLimit: permanentLimitMinorUnits,
          currency: snapshot.currency,
          expectedVersion: snapshot.version,
        },
        idempotencyKey: createCardManagementIdempotencyKey(),
      })
    },
    [creditCard?.id, limitsQuery.data, createLimitChange],
  )

  const billingContactQuery = useQuery({
    ...physicalCardManagementQueries.billingContact(creditCard?.id),
    // A spread override REPLACES the factory's arg-presence guard, so it is
    // re-stated. Declarative `enabled`, never an effect: with the flag off this
    // route does not exist in production and must not be called.
    enabled: showBillingContactApi && Boolean(creditCard?.id),
    retry: false,
  })

  const replaceBillingContact = useReplaceBillingContact()

  // Transport → form. `null` becomes `''` for the inputs;
  // `onSaveBillingContact` maps back on the way out, because a complete-set PUT
  // distinguishes them.
  //
  // When Card Management has not activated this family for the card, the SAME
  // design form is still rendered — populated from the credit card instead. The
  // page degrades the data source; the tile never falls back to the legacy
  // labels, which are not in the design.
  const billingContact: UpdatedBillingContact | undefined = (():
    | UpdatedBillingContact
    | undefined => {
    if (showBillingContactApi) {
      const data = billingContactQuery.data
      if (data === undefined) return undefined
      return {
        nameLine1: data.nameLine1,
        nameLine2: data.nameLine2 ?? '',
        mobilePhone: data.mobilePhone,
        address: {
          address1: data.billingAddress.line1,
          address2: data.billingAddress.line2 ?? '',
          city: data.billingAddress.city,
          province: data.billingAddress.province ?? '',
          postal: data.billingAddress.postal ?? '',
          country: data.billingAddress.country,
        },
      }
    }
    if (creditCard === undefined) return undefined
    // Degraded source: the card's own name/company/address, plus the profile
    // phone the legacy row used to show read-only.
    return {
      nameLine1: creditCard.displayName ?? '',
      nameLine2: creditCard.companyName ?? '',
      mobilePhone: currentUser?.phone ?? '',
      address: {
        address1: creditCard.address?.address1 ?? '',
        address2: creditCard.address?.address2 ?? '',
        city: creditCard.address?.city ?? '',
        province: creditCard.address?.province ?? '',
        postal: creditCard.address?.postal ?? '',
        country: creditCard.address?.country ?? '',
      },
    }
  })()

  // Ready as soon as there is something to render, whichever source it came
  // from — otherwise the degraded path would sit on "Loading" forever waiting
  // for a query it never fires.
  const billingContactState: 'loading' | 'ready' | 'error' =
    !capabilitiesResolved
      ? // Neither source is knowable yet — do not offer a save through either.
        'loading'
      : showBillingContactApi
        ? billingContactQuery.data !== undefined
          ? 'ready'
          : billingContactQuery.isError
            ? 'error'
            : 'loading'
        : billingContact !== undefined
          ? 'ready'
          : 'loading'

  const onSaveBillingContact = useCallback(
    (values: UpdatedBillingContact): void => {
      const creditCardIdForSave = creditCard?.id
      if (creditCardIdForSave === undefined) return

      if (!showBillingContactApi) {
        // Degraded path: the same form, persisted through the credit card the
        // legacy row used. The phone is NOT sent — it belongs to the user's
        // profile, not the card. (The form stopped exposing a phone control in
        // the 2026-08-11 design cleanup; the value is still round-tripped.)
        if (creditCard === undefined) return
        updateCreditCardMutation.mutate({
          creditCardId: creditCardIdForSave,
          body: {
            ...creditCard,
            displayName: values.nameLine1,
            companyName: values.nameLine2,
            country: values.address.country,
            address1: values.address.address1,
            address2: values.address.address2,
            city: values.address.city,
            province: values.address.province,
            postal: values.address.postal,
          },
        })
        return
      }

      const currentVersion = billingContactQuery.data?.version
      // No version means no fence. Sending without one (or with a guessed one)
      // is how a blind overwrite of someone else's concurrent edit happens.
      if (currentVersion === undefined) return

      // `''` → `null` on the way out: on a complete-set replace, null CLEARS
      // the field where the country's rules permit, and an empty string is a
      // different (usually invalid) value.
      const orNull = (value: string): string | null =>
        value.trim() === '' ? null : value

      replaceBillingContact.mutate({
        creditCardId: creditCardIdForSave,
        request: {
          nameLine1: values.nameLine1,
          nameLine2: orNull(values.nameLine2),
          billingAddress: {
            line1: values.address.address1,
            line2: orNull(values.address.address2),
            city: values.address.city,
            province: orNull(values.address.province),
            postal: orNull(values.address.postal),
            country: values.address.country,
          },
          mobilePhone: values.mobilePhone,
          expectedVersion: currentVersion,
        },
        // One key per attempt. Not regenerated on a network retry, and never
        // rotated to escape an in-flight or uncertain operation.
        idempotencyKey: createCardManagementIdempotencyKey(),
      })
    },
    [
      creditCard,
      showBillingContactApi,
      billingContactQuery.data?.version,
      replaceBillingContact,
      updateCreditCardMutation,
    ],
  )

  // Shared mapping for the Card Management mutation families. Each stale-version
  // code is family-specific by design — .5 limits, .17 status, .28 billing — so
  // they are passed in rather than guessed from the status code.
  const cardManagementErrorMessage = (
    error: QueryError | null | undefined,
    isError: boolean,
    staleCode: string,
  ): string | undefined => {
    if (!isError) return undefined
    const code = cardManagementErrorCode(error)
    if (code === staleCode) {
      return translate(
        'This card changed somewhere else. Refresh the page and try again.',
      )
    }
    if (code === CARD_MANAGEMENT_ERROR_CODES.statusConflict) {
      return translate("This card's current status does not allow that action.")
    }
    if (code === CARD_MANAGEMENT_ERROR_CODES.reconciliationRequired) {
      return translate(
        "We couldn't confirm whether your change was applied. Refresh before trying again.",
      )
    }
    if (code === CARD_MANAGEMENT_ERROR_CODES.operationActive) {
      return translate(
        'Another change to this card is still processing. Wait a moment and try again.',
      )
    }
    return translate("We couldn't save your changes. Please try again.")
  }

  const paymentChannelsSaveError = cardManagementErrorMessage(
    setPaymentChannel.error,
    setPaymentChannel.isError,
    // Payment channels have their OWN stale-version code.
    'CARD-MGMT.21',
  )

  const limitSaveError = cardManagementErrorMessage(
    createLimitChange.error,
    createLimitChange.isError,
    CARD_MANAGEMENT_ERROR_CODES.limitsStaleVersion,
  )

  // Two geofence-only failures the shared mapping cannot express: a country
  // that search offered but the card cannot actually use (search is a hint —
  // the mutation is authoritative), and a server- or partner-owned maximum
  // whose number the contract deliberately does not disclose. Both are dead
  // ends for THAT country, not a reason to retry the same click.
  const geofenceSaveError = ((): string | undefined => {
    if (!changeGeofenceCountry.isError) return undefined
    const code = cardManagementErrorCode(changeGeofenceCountry.error)
    if (code === CARD_MANAGEMENT_ERROR_CODES.geofenceInvalidCountry) {
      return translate('That country cannot be used on this card.')
    }
    if (code === CARD_MANAGEMENT_ERROR_CODES.geofenceCountryLimit) {
      return translate(
        'This card has reached its limit on permitted countries. Remove one before adding another.',
      )
    }
    return cardManagementErrorMessage(
      changeGeofenceCountry.error,
      changeGeofenceCountry.isError,
      CARD_MANAGEMENT_ERROR_CODES.geofenceStaleVersion,
    )
  })()

  // Distinct recoveries, not one "try again". A stale version means the user's
  // view is behind and a refresh fixes it; an uncertain outcome must NOT invite
  // an immediate resubmit.
  const billingContactSaveError = ((): string | undefined => {
    if (!replaceBillingContact.isError) return undefined
    const code = cardManagementErrorCode(replaceBillingContact.error)
    if (code === CARD_MANAGEMENT_ERROR_CODES.billingContactStaleVersion) {
      return translate(
        'These details changed somewhere else. Refresh the page and re-enter your changes.',
      )
    }
    if (code === CARD_MANAGEMENT_ERROR_CODES.reconciliationRequired) {
      return translate(
        "We couldn't confirm whether your change was applied. Refresh before trying again.",
      )
    }
    if (code === CARD_MANAGEMENT_ERROR_CODES.operationActive) {
      return translate(
        'Another change to this card is still processing. Wait a moment and try again.',
      )
    }
    return translate("We couldn't save your changes. Please try again.")
  })()

  const queryClient = useQueryClient()
  const toastDispatch = useDispatch()

  // Keep creditCard.physicalCard.status in sync after a capability change so
  // visibility/state recompute on the latest server state.
  const invalidateCreditCardDetail = useCallback((): void => {
    if (creditCard?.id === undefined) return
    queryClient.invalidateQueries({
      queryKey: creditCardsQueryKeys.detail(creditCard.id),
      exact: true,
    })
  }, [creditCard?.id, queryClient])

  // Hoisted out of the callback so `translate` doesn't have to be a dependency. useTranslate()
  // builds a new function on every render, so depending on it made this callback — and every
  // callback derived from it downstream — a new identity each render. That defeated the
  // memoization CardSettingsTile does deliberately (see its comment on
  // handlePhysicalEnrollmentComplete), and churned PhysicalCardVerifyStep's Pusher
  // bind/unbind on every AccountSettings render. A string dependency compares by value, so
  // this stays stable.
  const cardActiveToastMessage = translate('Success! Your card is now active.')

  // Same reason as `cardActiveToastMessage` above: a string dependency compares
  // by value, so the callback below keeps a stable identity across renders.
  const billingSavedToastMessage = translate('Billing address updated.')

  // Same reason again — see `cardActiveToastMessage`.
  const limitSavedToastMessage = translate('Spend limit updated.')

  /*
   * The billing drawer closes itself on a successful save, so this toast is the
   * only confirmation the user gets — the form it used to live in is gone by
   * then. Errors are NOT toasted: a failed save keeps the drawer open and
   * renders the message beside its action, where the field that needs fixing is.
   */
  /*
   * Same contract as the billing drawer below: the spend-limit drawer closes
   * itself on a successful save, so this toast is the only confirmation the
   * user gets. Errors are NOT toasted — a failed save keeps the drawer open and
   * renders the message beside its action, next to the amount to correct.
   */
  const onLimitSaved = useCallback((): void => {
    toastDispatch(
      createSetIsToastOpen(true, {
        toastMessage: <p>{limitSavedToastMessage}</p>,
      }),
    )
  }, [toastDispatch, limitSavedToastMessage])

  const onBillingContactSaved = useCallback((): void => {
    toastDispatch(
      createSetIsToastOpen(true, {
        toastMessage: <p>{billingSavedToastMessage}</p>,
      }),
    )
  }, [toastDispatch, billingSavedToastMessage])

  const onPhysicalCardFeedConnected = useCallback((): void => {
    toastDispatch(
      createSetIsToastOpen(true, {
        toastMessage: <p>{cardActiveToastMessage}</p>,
      }),
    )
    invalidateCreditCardDetail()
  }, [toastDispatch, cardActiveToastMessage, invalidateCreditCardDetail])

  const puttingCreditCard = useSelector(selectPuttingCreditCardFromState)
  const puttingCreditCardAccount = useSelector(
    selectPuttingCreditCardAccountFromState,
  )
  const puttingCreditCardBilling = useSelector(
    selectPuttingCreditCardBillingFromState,
  )
  const putCreditCardAccountCausedError = useSelector(
    selectPutCreditCardAccountCausedErrorFromState,
  )
  const putCreditCardBillingCausedError = useSelector(
    selectPutCreditCardBillingCausedErrorFromState,
  )
  const patchingCreditCard = useSelector(selectPatchingCreditCardFromState)
  const putCreditCardTopLevelError = useSelector(
    selectPutCreditCardTopLevelErrorFromState,
  )
  const putCreditCardErrorDetailMap = useSelector(
    selectPutCreditCardErrorDetailMapFromState,
  )
  const replaceCreditCardTopLevelError = useSelector(
    selectReplaceCreditCardTopLevelErrorFromState,
  )
  const replaceCreditCardErrorDetailMap = useSelector(
    selectReplaceCreditCardErrorDetailMapFromState,
  )
  const canUpdateCreditCard = !!creditCardPermissions?.canUpdateCreditCard
  const isOrgAdmin = !!currentUser?.hasOrgAdminPermissions

  const dispatch = useBackgroundDispatch()

  const prevPuttingAccount = usePrevious(puttingCreditCardAccount)
  const prevPuttingBilling = usePrevious(puttingCreditCardBilling)
  const prevPuttingAny = usePrevious(puttingCreditCard)

  const refreshCard = useCallback(() => {
    refetchCreditCard()
  }, [creditCardId /*, refetch */])

  useEffect(() => {
    const finished = prevPuttingAccount && !puttingCreditCardAccount
    const succeeded = finished && !putCreditCardAccountCausedError
    if (succeeded) refreshCard()
  }, [
    prevPuttingAccount,
    puttingCreditCardAccount,
    putCreditCardAccountCausedError,
    refreshCard,
  ])

  useEffect(() => {
    const finished = prevPuttingBilling && !puttingCreditCardBilling
    const succeeded = finished && !putCreditCardBillingCausedError
    if (succeeded) refreshCard()
  }, [
    prevPuttingBilling,
    puttingCreditCardBilling,
    putCreditCardBillingCausedError,
    refreshCard,
  ])

  useEffect(() => {
    const finished = prevPuttingAny && !puttingCreditCard
    const noTopLevelError =
      !replaceCreditCardTopLevelError &&
      Object.keys(replaceCreditCardErrorDetailMap ?? {}).length === 0

    if (finished && noTopLevelError) {
      refreshCard()
    }
  }, [
    prevPuttingAny,
    puttingCreditCard,
    replaceCreditCardTopLevelError,
    replaceCreditCardErrorDetailMap,
    refreshCard,
  ])

  const onBillingInformationSuccessfulSubmit = (
    flatFields: AccountBillingInformationFlatFields,
    countryFields: AccountBillingInformationCountryFields,
  ): void => {
    if (creditCard !== undefined) {
      dispatch(
        putCreditCard(
          creditCard.id,
          {
            ...creditCard,
            ...flatFields,
            ...countryFields,
          },
          false,
          {
            type: PUT_CREDIT_CARD_BILLING_INIT,
          },
        ),
      )
    }
  }

  const onAccountInformationSuccessfulSubmit = (
    flatFields: AccountInformationFlatFields,
  ): void => {
    if (creditCard !== undefined) {
      dispatch(
        putCreditCard(
          creditCard.id,
          {
            ...creditCard,
            ...flatFields,
            ...creditCard.address,
          },
          false,
          {
            type: PUT_CREDIT_CARD_ACCOUNT_INIT,
          },
        ),
      )
    }
  }

  const onCardInformationSuccessfulSubmit = (
    flatFields: CardInformationBoxFlatFields,
  ): void => {
    if (creditCard !== undefined) {
      dispatch(
        replaceCreditCard(creditCard.id, {
          ...flatFields,
        }),
      )
    }
  }

  // TODO: When physicalCard.status=BLOCKED but status=ACTIVE, we show the
  // blocked box and hide all account settings. Decide if billing/account info
  // should still be accessible since the block only affects physical card
  // verification, not the account itself.
  const showBlocked = creditCard?.isBlocked
  // EX-40694: suppress the static CVS-failed takeover for a recoverable card with confirmable
  // missing details, so the retry surfaces (tile / panel) mount below instead of a dead-end box.
  const showCVSVerificationFailed = shouldShowCvsVerificationFailedTakeover({
    isBlocked: showBlocked,
    physicalCard: {
      status: physicalCardStatus,
      statusReason: creditCard?.physicalCard?.statusReason ?? null,
    },
    // Suppression requires the retry flag too: with it off the takeover must
    // render exactly as pre-EX-40694, since the surfaces below stay hidden.
    hasConfirmableMissingDetails:
      isConfirmableMissingDetails && confirmCardDetailsRetryEnabled,
  })
  const showVerificationRequired =
    !showBlocked &&
    !showCVSVerificationFailed &&
    creditCard?.physicalCard.status === PhysicalCardStatus.NeedsCVSVerification

  return creditCard !== undefined ? (
    <BackgroundPageContent>
      {showBlocked ? (
        <AccountVerificationBlockedBox
          secondParagraph={translate(
            'There have been too many failed attempts at verifying the account. In order to proceed, contact us via our ${resourceCenter}.',
            {
              resourceCenter: (
                <ResourceCenterLink
                  underline
                  css={css`
                    font-family: ${styleConstants.fontFamilies.regular};
                    font-size: 16px;
                    &:hover {
                      font-family: ${styleConstants.fontFamilies.regular};
                      font-size: 16px;
                    }
                  `}
                />
              ),
            },
          )}
        />
      ) : showCVSVerificationFailed ? (
        <AccountVerificationBlockedBox
          secondParagraph={translate(
            'Please check your card information and try enrolling again, or contact us via our ${resourceCenter}.',
            {
              resourceCenter: (
                <ResourceCenterLink
                  underline
                  css={css`
                    font-family: ${styleConstants.fontFamilies.regular};
                    font-size: 16px;
                    &:hover {
                      font-family: ${styleConstants.fontFamilies.regular};
                      font-size: 16px;
                    }
                  `}
                />
              ),
            },
          )}
        />
      ) : showVerificationRequired ? (
        <AccountVerificationRequiredBox
          accountResendVerificationError={
            startVerificationMutation.error?.error
          }
          accountVerificationError={verifyMutation.error?.error}
          canVerifyAgain={creditCard.canVerifyAgain}
          handleResendVerification={() =>
            startVerificationMutation.mutate({ creditCardId })
          }
          handleVerifyCreditCard={(code) =>
            verifyMutation.mutate({ creditCardId, body: { code } })
          }
          postingAccountVerification={verifyMutation.isPending}
          postingResendAccountVerification={startVerificationMutation.isPending}
          timeUntilNextVerificationRequested={
            creditCard.timeUntilNextVerificationRequested
          }
          canUpdateCreditCard={canUpdateCreditCard}
        />
      ) : showUpdatedPhysicalCardSettings ? (
        <UpdatedPhysicalCardSettings
          creditCard={creditCard}
          countryOptions={countryOptions}
          // Same permission the legacy boxes gate editing on — without it the
          // tile must not offer a Save that PUT /creditcards/:id would reject.
          // On the connected billing path the server's own MANAGE verdict is
          // the edit gate; the legacy card permission still governs the
          // degraded PUT /creditcards/:id path.
          canEditCardDetails={
            showBillingContactApi
              ? canManageBillingContact
              : canUpdateCreditCard
          }
          merchantCategoryRestrictions={merchantCategoryRestrictions}
          cardManagementStatus={cardManagementStatus}
          lifecycleControls={lifecycleControls}
          canApplySpendLimitDirectly={isOrgAdmin}
          // The physical-feed switch opens the real enroll flow, so it carries
          // the same PHYSICAL_CARD_TRANSACTIONS_FEED entitlement gate
          // PhysicalCardTransactionsPanel applies to its Connect CTA.
          canConnectPhysicalCardFeed={canConnect}
          confirmCardDetailsEnabled={confirmCardDetailsEnabled}
          confirmCardDetailsRetryEnabled={confirmCardDetailsRetryEnabled}
          // Both panels this disclosure replaces are Extend-brand only.
          showConnectedCapabilities={isExtendBrand}
          billingEditDeniedReason={billingEditDeniedReason}
          billingContact={billingContact}
          billingContactState={billingContactState}
          onSaveBillingContact={onSaveBillingContact}
          onBillingContactSaved={onBillingContactSaved}
          isSavingBillingContact={
            showBillingContactApi
              ? replaceBillingContact.isPending
              : updateCreditCardMutation.isPending
          }
          billingContactSaveError={
            showBillingContactApi
              ? billingContactSaveError
              : updateCreditCardMutation.isError
                ? translate("We couldn't save your changes. Please try again.")
                : undefined
          }
          // Limits + freeze: capability-gated, so no client flag. The server
          // decides, and a denied verdict renders as "unavailable" rather than
          // as a retryable error.
          useLimitsApi={showUpdatedPhysicalCardSettings}
          limits={limitsQuery.data}
          limitsState={limitsState}
          onApplyPermanentLimit={onApplyPermanentLimit}
          isApplyingLimit={createLimitChange.isPending}
          limitSaveError={limitSaveError}
          limitSaveSucceeded={createLimitChange.isSuccess}
          onLimitSaved={onLimitSaved}
          canApplyLimitChange={canApplyLimitChange}
          companyLine={
            companyLineQuery.data && {
              name: companyLineQuery.data.name,
              totalCredit: companyLineQuery.data.totalCredit,
              // Signed on purpose — a negative line is a real state the
              // contract requires displaying rather than clamping.
              availableCredit: companyLineQuery.data.availableCredit,
              // The line's own currency, not the card's — see CompanyLineSummary.
              currency: companyLineQuery.data.currency,
            }
          }
          usePaymentChannelsApi={showUpdatedPhysicalCardSettings}
          paymentChannels={paymentChannelsQuery.data?.channels}
          paymentChannelsState={paymentChannelsState}
          onSetPaymentChannel={
            canManagePaymentChannels ? onSetPaymentChannel : undefined
          }
          pendingPaymentChannel={
            setPaymentChannel.isPending && setPaymentChannel.variables
              ? {
                  channel: setPaymentChannel.variables.channel,
                  // The value the user asked for, so the row can hold it while
                  // the write is in flight instead of springing back.
                  enabled: setPaymentChannel.variables.request.enabled,
                }
              : undefined
          }
          paymentChannelsSaveError={paymentChannelsSaveError}
          // Attribute the failure to the channel actually flipped, so the
          // message lands on that row rather than at the foot of the list.
          failedPaymentChannel={
            setPaymentChannel.isError
              ? setPaymentChannel.variables?.channel
              : undefined
          }
          savedPaymentChannel={
            setPaymentChannel.isSuccess
              ? setPaymentChannel.variables?.channel
              : undefined
          }
          // EX-40629 — geofence. The snapshot's own availability is the gate,
          // so the handler is passed unconditionally and the tile withholds the
          // controls on a READ_ONLY or UNAVAILABLE snapshot.
          geofence={
            geofenceQuery.data && {
              permittedCountries: geofenceQuery.data.permittedCountries,
              availability: geofenceQuery.data.availability,
              reasonCode: geofenceQuery.data.reasonCode,
              version: geofenceQuery.data.version,
            }
          }
          geofenceState={geofenceState}
          geofenceCountryQuery={geofenceCountryQuery}
          onGeofenceCountryQueryChange={setGeofenceCountryQuery}
          geofenceCountryResults={geofenceCountriesQuery.data?.items}
          geofenceCountrySearchState={
            geofenceCountriesQuery.data !== undefined
              ? 'ready'
              : geofenceCountriesQuery.isError
                ? 'error'
                : 'loading'
          }
          onChangeGeofenceCountry={
            canManageGeofence ? onChangeGeofenceCountry : undefined
          }
          pendingGeofenceCountry={
            changeGeofenceCountry.isPending && changeGeofenceCountry.variables
              ? {
                  change: changeGeofenceCountry.variables.request.change,
                  countryCode:
                    changeGeofenceCountry.variables.request.countryCode,
                }
              : undefined
          }
          geofenceSaveError={geofenceSaveError}
          onPhysicalCardFeedConnected={onPhysicalCardFeedConnected}
          onPhysicalCardFeedDisconnected={invalidateCreditCardDetail}
        />
      ) : (
        <HorizontalContentCollection>
          <VerticalContentCollection>
            <AccountInformationBox
              creditCard={creditCard}
              topLevelError={putCreditCardTopLevelError}
              puttingCreditCard={puttingCreditCard}
              puttingCreditCardAccount={puttingCreditCardAccount}
              onSuccessfulSubmit={onAccountInformationSuccessfulSubmit}
              hasEditPermissions={canUpdateCreditCard}
              inFocus={putCreditCardAccountCausedError}
            />
            <AccountBillingInformationBox
              creditCard={creditCard}
              puttingCreditCard={puttingCreditCard}
              puttingCreditCardBilling={puttingCreditCardBilling}
              topLevelError={putCreditCardTopLevelError}
              errorDetailMap={putCreditCardErrorDetailMap}
              onSuccessfulSubmit={onBillingInformationSuccessfulSubmit}
              hasEditPermissions={canUpdateCreditCard}
              inFocus={putCreditCardBillingCausedError}
            />
          </VerticalContentCollection>
          <VerticalContentCollection>
            {!creditCard.partner?.isVirtualAccount && (
              <TestId testId={'cardInformationBox'}>
                <CardInformationBox
                  creditCard={creditCard}
                  puttingCreditCard={puttingCreditCard}
                  patchingCreditCard={patchingCreditCard}
                  topLevelError={replaceCreditCardTopLevelError}
                  errorDetailMap={replaceCreditCardErrorDetailMap}
                  onSuccessfulSubmit={onCardInformationSuccessfulSubmit}
                  hasEditPermissions={
                    creditCard.features.rcnReplacementEnabled &&
                    canUpdateCreditCard
                  }
                />
              </TestId>
            )}
            {isExtendBrand && showPhysicalCardTransactionsPanel && (
              <PhysicalCardTransactionsPanel
                creditCard={creditCard}
                canConnect={canConnect}
                confirmCardDetailsEnabled={confirmCardDetailsEnabled}
                confirmCardDetailsRetryEnabled={confirmCardDetailsRetryEnabled}
              />
            )}
            {isExtendBrand && (
              <VirtualCardCapabilitiesPanel creditCard={creditCard} />
            )}
          </VerticalContentCollection>
        </HorizontalContentCollection>
      )}
    </BackgroundPageContent>
  ) : (
    <NotFoundBackgroundPageContent />
  )
}

export default AccountSettings
